/**
 * WebRTC Media Manager
 * 
 * Handles webcam/microphone access for SpellTable-like webcam mode.
 * Part of Phase 5: Webcam Mode.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// ============================================
// TYPES
// ============================================

export interface MediaDevice {
    deviceId: string;
    label: string;
    kind: 'videoinput' | 'audioinput' | 'audiooutput';
}

export interface MediaSettings {
    videoDeviceId: string | null;
    audioInputDeviceId: string | null;
    audioOutputDeviceId: string | null;
    videoEnabled: boolean;
    audioEnabled: boolean;
}

export interface MediaState {
    isReady: boolean;
    isLoading: boolean;
    error: string | null;
    stream: MediaStream | null;
    devices: {
        videoInputs: MediaDevice[];
        audioInputs: MediaDevice[];
        audioOutputs: MediaDevice[];
    };
    settings: MediaSettings;
}

// ============================================
// DEFAULT SETTINGS
// ============================================

const DEFAULT_SETTINGS: MediaSettings = {
    videoDeviceId: null,
    audioInputDeviceId: null,
    audioOutputDeviceId: null,
    videoEnabled: true,
    audioEnabled: true,
};

// ============================================
// MEDIA MANAGER HOOK
// ============================================

export function useMediaManager() {
    const [state, setState] = useState<MediaState>({
        isReady: false,
        isLoading: false,
        error: null,
        stream: null,
        devices: {
            videoInputs: [],
            audioInputs: [],
            audioOutputs: [],
        },
        settings: DEFAULT_SETTINGS,
    });

    const streamRef = useRef<MediaStream | null>(null);

    // Enumerate available devices
    const enumerateDevices = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();

            const videoInputs: MediaDevice[] = [];
            const audioInputs: MediaDevice[] = [];
            const audioOutputs: MediaDevice[] = [];

            devices.forEach((device) => {
                const mediaDevice: MediaDevice = {
                    deviceId: device.deviceId,
                    label: device.label || `${device.kind} (${device.deviceId.slice(0, 8)})`,
                    kind: device.kind as MediaDevice['kind'],
                };

                if (device.kind === 'videoinput') {
                    videoInputs.push(mediaDevice);
                } else if (device.kind === 'audioinput') {
                    audioInputs.push(mediaDevice);
                } else if (device.kind === 'audiooutput') {
                    audioOutputs.push(mediaDevice);
                }
            });

            setState(prev => ({
                ...prev,
                devices: { videoInputs, audioInputs, audioOutputs },
            }));

            return { videoInputs, audioInputs, audioOutputs };
        } catch (error) {
            console.error('Failed to enumerate devices:', error);
            setState(prev => ({
                ...prev,
                error: 'Failed to list media devices',
            }));
            return null;
        }
    }, []);

    // Request media stream with specified devices
    const requestStream = useCallback(async (settings: Partial<MediaSettings> = {}) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        // Stop existing stream
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        const mergedSettings = { ...state.settings, ...settings };

        try {
            const constraints: MediaStreamConstraints = {
                video: mergedSettings.videoEnabled ? {
                    deviceId: mergedSettings.videoDeviceId ? { exact: mergedSettings.videoDeviceId } : undefined,
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 30 },
                } : false,
                audio: mergedSettings.audioEnabled ? {
                    deviceId: mergedSettings.audioInputDeviceId ? { exact: mergedSettings.audioInputDeviceId } : undefined,
                    echoCancellation: true,
                    noiseSuppression: true,
                } : false,
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            // Re-enumerate devices after permission granted (labels become available)
            await enumerateDevices();

            setState(prev => ({
                ...prev,
                isReady: true,
                isLoading: false,
                stream,
                settings: mergedSettings,
            }));

            return stream;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to access media devices';
            console.error('Media request failed:', error);

            setState(prev => ({
                ...prev,
                isReady: false,
                isLoading: false,
                error: errorMessage,
            }));

            return null;
        }
    }, [state.settings, enumerateDevices]);

    // Stop all media tracks
    const stopStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        setState(prev => ({
            ...prev,
            isReady: false,
            stream: null,
        }));
    }, []);

    // Toggle video
    const toggleVideo = useCallback(() => {
        if (streamRef.current) {
            const videoTrack = streamRef.current.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled;
                setState(prev => ({
                    ...prev,
                    settings: { ...prev.settings, videoEnabled: videoTrack.enabled },
                }));
            }
        }
    }, []);

    // Toggle audio
    const toggleAudio = useCallback(() => {
        if (streamRef.current) {
            const audioTrack = streamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setState(prev => ({
                    ...prev,
                    settings: { ...prev.settings, audioEnabled: audioTrack.enabled },
                }));
            }
        }
    }, []);

    // Change video device
    const setVideoDevice = useCallback(async (deviceId: string) => {
        return requestStream({ videoDeviceId: deviceId });
    }, [requestStream]);

    // Change audio input device
    const setAudioInputDevice = useCallback(async (deviceId: string) => {
        return requestStream({ audioInputDeviceId: deviceId });
    }, [requestStream]);

    // Set audio output device (on supported elements)
    const setAudioOutputDevice = useCallback(async (deviceId: string, element?: HTMLMediaElement) => {
        if (element && 'setSinkId' in element) {
            try {
                await (element as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(deviceId);
                setState(prev => ({
                    ...prev,
                    settings: { ...prev.settings, audioOutputDeviceId: deviceId },
                }));
                return true;
            } catch (error) {
                console.error('Failed to set audio output:', error);
                return false;
            }
        }
        return false;
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Initial device enumeration
    useEffect(() => {
        enumerateDevices();
    }, [enumerateDevices]);

    return {
        ...state,
        requestStream,
        stopStream,
        toggleVideo,
        toggleAudio,
        setVideoDevice,
        setAudioInputDevice,
        setAudioOutputDevice,
        enumerateDevices,
    };
}

// ============================================
// VIDEO ELEMENT REF HOOK
// ============================================

export function useVideoStream(stream: MediaStream | null) {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
        }
    }, [stream]);

    return videoRef;
}

// ============================================
// PEER CONNECTION TYPES (for future WebRTC P2P)
// ============================================

export interface PeerConnection {
    peerId: string;
    peerName: string;
    seatIndex: number;
    stream: MediaStream | null;
    connection: RTCPeerConnection | null;
    isConnected: boolean;
    isMuted: boolean;
    isVideoOff: boolean;
}

export interface SessionPeers {
    localStream: MediaStream | null;
    peers: Map<string, PeerConnection>;
    isHost: boolean;
}

// ============================================
// GRID LAYOUT UTILITIES
// ============================================

export type GridLayout = '1x1' | '2x1' | '2x2' | '3x2' | '4x2';

export function getGridLayout(playerCount: number): GridLayout {
    if (playerCount <= 1) return '1x1';
    if (playerCount <= 2) return '2x1';
    if (playerCount <= 4) return '2x2';
    if (playerCount <= 6) return '3x2';
    return '4x2';
}

export function getGridStyles(layout: GridLayout): { gridTemplateColumns: string; gridTemplateRows: string } {
    switch (layout) {
        case '1x1':
            return { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' };
        case '2x1':
            return { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' };
        case '2x2':
            return { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' };
        case '3x2':
            return { gridTemplateColumns: '1fr 1fr 1fr', gridTemplateRows: '1fr 1fr' };
        case '4x2':
            return { gridTemplateColumns: '1fr 1fr 1fr 1fr', gridTemplateRows: '1fr 1fr' };
    }
}

// ============================================
// DEVICE SELECTION MODAL CONFIG
// ============================================

export interface DeviceSelectionConfig {
    showVideo: boolean;
    showAudioInput: boolean;
    showAudioOutput: boolean;
    allowMobileWebcam: boolean;  // Experimental: use phone as webcam
}

export const DEFAULT_DEVICE_SELECTION_CONFIG: DeviceSelectionConfig = {
    showVideo: true,
    showAudioInput: true,
    showAudioOutput: true,
    allowMobileWebcam: false,
};
